import React, {Fragment, useState, useEffect, useCallback} from "react";
import Card from "components/Cards/Card";
import trendScoresIcon from "assets/Icon_Tab_09.png";
import {Dropdown, SelectOption} from "components/ui/Dropdown";
import {setChartSelection, getChartSelection} from "helpers/chartSelections";
import {v4 as uuidv4} from "uuid";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {useCurrentPng} from "recharts-to-png";
import {ImageMap} from "../../Dashboard/Dashboard";
import LoadingIndicator from "../../ui/LoadingIndicator";
import PortalTooltip from "../PortalTooltip";

type TrendScoresProps = {
  href: string | null;
  data: ChartData;
  defaultChartType: string;
  questions: Questions[];
  customName?: string;
  setImages?: React.Dispatch<React.SetStateAction<ImageMap>>;
  idx?: number;
  isLoading?: boolean; // Add loading prop
};

type Questions = {
  value: string;
  label: string;
};

type ChartData = {
  pieChartData: any;
  otherChartData: any[];
  options: {
    yAxisType: "Count" | "Percentage" | "Score";
  };
  questions?: string[];
  periods?: string[];
};

const chartColours = [
  "#307bb9",
  "#30b862",
  "#a440a4",
  "#d5a438",
  "#bb4040",
  "#008272",
  "#4068b4",
  "#7ebce6",
  "#8fba34",
  "#f64250",
  "#fbbf22",
  "#94adbf",
];

const checkNullChartData = (c: any[]) => {
  const data = c.find((v) => v);
  return !!data;
};

function percentageFormatter(item: any) {
  return parseFloat(item).toFixed(1) + "%";
}

function avgFormatter(item: any) {
  return item.toFixed(1);
}

function scoreFormatter(item: any) {
  return parseFloat(item).toFixed(2);
}

const scoreTicks = [0, 1, 2, 3, 4, 5];
const scoreDomain: [number, number] = [0, 5];

function getFormatter(yAxisType: string | undefined) {
  if (yAxisType === "Percentage") return percentageFormatter;
  if (yAxisType === "Score") return scoreFormatter;
  return avgFormatter;
}

function getYAxisDomain(yAxisType: string | undefined, data: any[], keys?: string[]) {
  if (yAxisType === "Percentage") return getPercentageDomain(data, keys);
  if (yAxisType === "Score") return scoreDomain;
  return undefined;
}

function getYAxisTicks(yAxisType: string | undefined, data: any[], keys?: string[]) {
  if (yAxisType === "Percentage") return getPercentageTicks(data, keys);
  if (yAxisType === "Score") return scoreTicks;
  return undefined;
}

const TrendTick: React.FC<any> = ({
                                    payload: {value},
                                    verticalAnchor,
                                    visibleTicksCount,
                                    ...rest
                                  }) => (
    <Text {...rest} verticalAnchor={verticalAnchor} visibleTicksCount={visibleTicksCount} width={50}>
      {value}
    </Text>
);

const ticks20 = [0, 20, 40, 60, 80, 100];

function getMinValue(data: any[], keys?: string[]): number {
  let min = 0;
  if (!data || !data.length) return 0;
  data.forEach((item: any) => {
    if (keys && keys.length) {
      keys.forEach((k) => {
        const v = item[k];
        if (typeof v === "number" && v < min) min = v;
      });
    } else {
      Object.entries(item).forEach(([k, v]) => {
        if (k !== "name" && typeof v === "number" && (v as number) < min) min = v as number;
      });
    }
  });
  return min;
}

function getPercentageDomain(data: any[], keys?: string[]): [number, number] {
  const min = getMinValue(data, keys);
  if (min >= 0) return [0, 100];
  const roundedMin = Math.floor(min / 20) * 20;
  return [Math.max(roundedMin, -100), 100];
}

function getPercentageTicks(data: any[], keys?: string[]): number[] {
  const min = getMinValue(data, keys);
  if (min >= 0) return ticks20;
  const roundedMin = Math.floor(min / 20) * 20;
  const start = Math.max(roundedMin, -100);
  const ticks: number[] = [];
  for (let i = start; i <= 100; i += 20) {
    ticks.push(i);
  }
  return ticks;
}


const TrendScores: React.FC<TrendScoresProps> = ({
                                                   href,
                                                   data,
                                                   defaultChartType,
                                                   questions,
                                                   customName,
                                                   setImages,
                                                   idx,
                                                   isLoading = true // Default to false
                                                 }) => {
  const [trendScoresChartType, setTrendScoresChartType] = useState(
      defaultChartType ? defaultChartType : "Area"
  );
  // Track internal loading state
  const [internalLoading, setInternalLoading] = useState(true);

  // Effect to check if data is ready
  useEffect(() => {
    if (data && data.otherChartData) {
      // Set a small delay to prevent flashing
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [data]);

  // Determine if we should show loading state
  const showLoading = isLoading || internalLoading;

  let trendScoresQuestions: string = "";
  let trendScoresCategories = [
    {
      label: "",
      value: "",
    },
  ];

  const chartId = `trendScores_${customName || "default"}`;

  const [trendScoresCategory, setTrendScoresCategory] = useState<SelectOption>(() => {
    const stored = getChartSelection(chartId);
    return stored || trendScoresCategories[0];
  });

  const handleTrendScoresCategorySelect = (selectedOption: SelectOption) => {
    setTrendScoresCategory(selectedOption);
    setChartSelection(chartId, selectedOption);
  };

  // Listen for sync event from PDF generator
  useEffect(() => {
    const handleSync = () => {
      const stored = getChartSelection(chartId);
      if (stored) setTrendScoresCategory(stored);
    };
    window.addEventListener("pdf-sync-selections", handleSync);
    return () => window.removeEventListener("pdf-sync-selections", handleSync);
  }, [chartId]);

  const handleSetTrendScoresChartType = (value: string) => {
    setTrendScoresChartType(value);
  };

  if (questions) {
    trendScoresCategories = [];
    questions.forEach((q: any) => {
      trendScoresCategories.push(q);
    });
    trendScoresCategories.push({
      label: "All categories",
      value: "All",
    });
    if (trendScoresCategory.label == "")
      setTrendScoresCategory(trendScoresCategories[0])

    const questionsAsObject: Record<string, string> = {};
    for (const {label, value: key} of questions) {
      questionsAsObject[key] = label;
    }
    trendScoresQuestions = JSON.stringify(questionsAsObject);
  }

  const uuid = uuidv4();

  const [getPng, {ref}] = useCurrentPng();
  const [savedPng, setSavedPng] = useState<boolean>(false);

  async function saveChart() {
    let runningInterval = setInterval(async () => {
      if (savedPng) {
        clearInterval(runningInterval);
      }
      try {
        const png = await getPng();

        // Verify that png is not undefined
        if (png && setImages && idx) {
          setImages((oldState: ImageMap) => {
            return {...oldState, [idx]: png}
          });
          setSavedPng(true);
          clearInterval(runningInterval);
        }
      } catch (error) {
        // console.log(error)
      }
    }, 1000)
  }

  // If still loading, show the loading indicator regardless of data state
  if (showLoading) {
    return (
        <Card
            href={href}
            icon={trendScoresIcon}
            title={customName ? customName : "TREND SCORES"}
            chartOptions={
              <React.Fragment>
                <button className="Option" onClick={() => handleSetTrendScoresChartType("Area")}>
                  Area
                </button>
                <button className="Option" onClick={() => handleSetTrendScoresChartType("Bar")}>
                  Bar
                </button>
                <button className="Option" onClick={() => handleSetTrendScoresChartType("Line")}>
                  Line
                </button>
              </React.Fragment>
            }
        >
          <div className="CardBody">
            <div style={{display: "flex", justifyContent: "center", padding: "10px 15px 0", overflow: "hidden"}}>
              <div style={{maxWidth: "100%", width: "250px"}}>
                <Dropdown
                    name="TrendCategory"
                    value={trendScoresCategory}
                    options={trendScoresCategories}
                    type="FullWidth"
                    onChange={handleTrendScoresCategorySelect}
                />
              </div>
            </div>
            <div className="Chart" style={{height: "300px"}}>
              <LoadingIndicator />
            </div>
          </div>
        </Card>
    );
  }

  // Hide the entire component if we have less than 2 data points (after loading)
  if (!data?.otherChartData || data?.otherChartData.length < 2) {
    // 'Trendscoreschart' to be suppressed when only one data grouping
    return <Fragment/>
  }

  return (
      <Card
          href={href}
          icon={trendScoresIcon}
          title={customName ? customName : "TREND SCORES"}
          chartOptions={
            <React.Fragment>
              <button className="Option" onClick={() => handleSetTrendScoresChartType("Area")}>
                Area
              </button>
              <button className="Option" onClick={() => handleSetTrendScoresChartType("Bar")}>
                Bar
              </button>
              <button className="Option" onClick={() => handleSetTrendScoresChartType("Line")}>
                Line
              </button>
            </React.Fragment>
          }
      >
        <div className="CardBody">
          <div style={{display: "flex", justifyContent: "center", padding: "10px 15px 0", overflow: "hidden"}}>
            <div style={{maxWidth: "100%", width: "250px"}}>
              <Dropdown
                  name="TrendCategory"
                  value={trendScoresCategory}
                  options={trendScoresCategories}
                  type="FullWidth"
                  onChange={handleTrendScoresCategorySelect}
              />
            </div>
          </div>
          <div className="Chart"  style={{height: "300px"}}>
            <div className="ChartWrapper">
              {data && checkNullChartData(data.pieChartData.data) ? (() => {
                    const calcMw = ((data.questions && trendScoresChartType === "Area") ||
                            trendScoresChartType === "Line"
                                ? 100
                                : data.questions && data.questions.length > 1
                                    ? trendScoresCategory.value === "All"
                                        ? data.questions.length * 50
                                        : 100
                                    : 100) * data.pieChartData.periods.length - 2;
                    return <div
                      className={calcMw > 400 ? "scrollable-chart" : undefined}
                      style={{ minWidth: `${calcMw}px` }}
                  >
                    <ResponsiveContainer width="99%" height={270} key={uuid}>
                      {trendScoresChartType === "Area" ? (
                          <AreaChart
                              data={data.otherChartData}
                              margin={{top: 20, bottom: 20, right: 20}}
                              ref={ref}
                          >
                            <CartesianGrid vertical={false} strokeDasharray="3"/>
                            <Tooltip
                                formatter={
                                  getFormatter(data.options.yAxisType)
                                }
                                allowEscapeViewBox={{x: true, y: true}}
                                coordinate={{x: 0, y: 300}}
                            />
                            <XAxis
                                dataKey="name"
                                tick={<TrendTick/>}
                                axisLine={false}
                                tickLine={false}
                                padding={{left: 50, right: 50}}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={
                                  getFormatter(data.options.yAxisType)
                                }
                                domain={getYAxisDomain(data.options.yAxisType, data.otherChartData, trendScoresCategory.value === "All" ? data.questions : [trendScoresCategory.label])}
                                ticks={getYAxisTicks(data.options.yAxisType, data.otherChartData, trendScoresCategory.value === "All" ? data.questions : [trendScoresCategory.label])}
                            />
                            {trendScoresCategory.value === "All" && data.questions ? (
                                data.questions.map((q: string, i: number) => {
                                  return (
                                      <Area
                                          dataKey={q}
                                          fill={chartColours[i]}
                                          isAnimationActive={false}
                                          type="monotone"
                                          stroke={chartColours[i]}
                                          dot={true}
                                          fillOpacity=".1"
                                          key={"TrendScoresArea" + i}
                                      >
                                        <LabelList
                                            dataKey={q}
                                            position="top"
                                            formatter={
                                              data ? getFormatter(data.options.yAxisType) : avgFormatter
                                            }
                                        />
                                      </Area>
                                  );
                                })
                            ) : (
                                <Area
                                    dataKey={trendScoresCategory.label}
                                    fill={chartColours[0]}
                                    isAnimationActive={false}
                                    type="monotone"
                                    stroke={chartColours[0]}
                                    dot={true}
                                    fillOpacity=".1"
                                >
                                  <LabelList
                                      dataKey={trendScoresCategory.label}
                                      position="top"
                                      formatter={
                                        data ? getFormatter(data.options.yAxisType) : avgFormatter
                                      }
                                  />
                                </Area>
                            )}
                          </AreaChart>
                      ) : trendScoresChartType === "Bar" ? (
                          <BarChart
                              data={data.otherChartData}
                              margin={{top: 20, bottom: 20, right: 20}}
                              maxBarSize={65}
                              barGap={15}
                              ref={ref}
                          >
                            <CartesianGrid vertical={false} strokeDasharray="3"/>
                            <Tooltip
                                content={<PortalTooltip formatter={
                                  getFormatter(data.options.yAxisType)
                                } />}
                            />
                            <XAxis
                                dataKey="name"
                                tick={<TrendTick/>}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={
                                  getFormatter(data.options.yAxisType)
                                }
                                domain={getYAxisDomain(data.options.yAxisType, data.otherChartData, trendScoresCategory.value === "All" ? data.questions : [trendScoresCategory.label])}
                                ticks={getYAxisTicks(data.options.yAxisType, data.otherChartData, trendScoresCategory.value === "All" ? data.questions : [trendScoresCategory.label])}
                            />
                            {trendScoresCategory.value === "All" && data.questions ? (
                                data.questions.map((q: string, i: number) => {
                                  return (
                                      <Bar
                                          dataKey={q}
                                          fill={chartColours[i]}
                                          radius={[3, 3, 0, 0]}
                                          isAnimationActive={false}
                                      >
                                        <LabelList
                                            dataKey={q}
                                            position="top"
                                            formatter={
                                              data ? getFormatter(data.options.yAxisType) : avgFormatter
                                            }
                                        />
                                      </Bar>
                                  );
                                })
                            ) : (
                                <Bar
                                    dataKey={trendScoresCategory.label}
                                    fill={chartColours[0]}
                                    radius={[3, 3, 0, 0]}
                                    isAnimationActive={false}
                                >
                                  <LabelList
                                      dataKey={trendScoresCategory.label}
                                      position="top"
                                      formatter={
                                        data ? getFormatter(data.options.yAxisType) : avgFormatter
                                      }
                                  />
                                </Bar>
                            )}
                          </BarChart>
                      ) : trendScoresChartType === "Line" ? (
                          <LineChart
                              data={data.otherChartData}
                              margin={{top: 20, bottom: 20, right: 20}}
                              ref={ref}
                          >
                            <CartesianGrid vertical={false} strokeDasharray="3"/>
                            <Tooltip
                                content={<PortalTooltip formatter={
                                  getFormatter(data.options.yAxisType)
                                } />}
                            />
                            <XAxis
                                dataKey="name"
                                tick={<TrendTick/>}
                                axisLine={false}
                                tickLine={false}
                                padding={{left: 50, right: 50}}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={
                                  getFormatter(data.options.yAxisType)
                                }
                                domain={getYAxisDomain(data.options.yAxisType, data.otherChartData, trendScoresCategory.value === "All" ? data.questions : [trendScoresCategory.label])}
                                ticks={getYAxisTicks(data.options.yAxisType, data.otherChartData, trendScoresCategory.value === "All" ? data.questions : [trendScoresCategory.label])}
                            />
                            {trendScoresCategory.value === "All" && data.questions ? (
                                data.questions.map((q: string, i: number) => {
                                  return (
                                      <Line
                                          key={i}
                                          dataKey={q}
                                          fill={chartColours[i]}
                                          isAnimationActive={false}
                                          type="monotone"
                                          stroke={chartColours[i]}
                                          dot={{
                                            stroke: chartColours[i],
                                            strokeWidth: 2,
                                          }}
                                      >
                                        <LabelList
                                            dataKey={q}
                                            position="top"
                                            formatter={
                                              data ? getFormatter(data.options.yAxisType) : avgFormatter
                                            }
                                        />
                                      </Line>
                                  );
                                })
                            ) : (
                                <Line
                                    dataKey={trendScoresCategory.label}
                                    fill={chartColours[0]}
                                    isAnimationActive={false}
                                    type="monotone"
                                    stroke={chartColours[0]}
                                    dot={{
                                      stroke: chartColours[0],
                                      strokeWidth: 2,
                                    }}
                                >
                                  <LabelList
                                      dataKey={trendScoresCategory.label}
                                      position="top"
                                      formatter={
                                        data ? getFormatter(data.options.yAxisType) : avgFormatter
                                      }
                                  />
                                </Line>
                            )}
                          </LineChart>
                      ) : (
                          <React.Fragment/>
                      )}
                    </ResponsiveContainer>
                  </div>;
                  })() : (
                  <div className="NoChartData">
                    <p>No results found</p>
                  </div>
              )}
            </div>
          </div>
        </div>
      </Card>
  );
};

export default TrendScores;